import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Droplet, ChevronLeft, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import type { JoinCompanyRequest, JoinCompanyResponse } from '../../../shared/contracts';

export default function Join() {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleJoin = async () => {
    if (!inviteCode.trim()) {
      setError('Please enter an invite code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const request: JoinCompanyRequest = {
        inviteCode: inviteCode.toUpperCase().trim(),
      };

      console.log('[Join] Attempting to join with code:', request.inviteCode);

      const response = await api.post<JoinCompanyResponse>('/onboarding/join', request);

      console.log('[Join] Join response:', response);

      if (response.success) {
        // Update user in context with updated onboarding status
        updateUser(response.user);
        navigate('/dashboard');
      } else {
        setError(response.error || 'Failed to join company. Please try again.');
      }
    } catch (err: any) {
      console.error('[Join] Error:', err);
      const errorMessage = err?.response?.data?.error || err?.message || 'Invalid invite code or connection error';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading && inviteCode.trim()) {
      handleJoin();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/welcome')}
            className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700 mb-6 font-medium"
          >
            <ChevronLeft className="h-5 w-5" />
            Back
          </button>

          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-white rounded-full p-4 shadow-lg">
                <Droplet className="h-12 w-12 text-cyan-600" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Join Company</h1>
            <p className="text-gray-600">Enter the invite code from your company admin</p>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Invite Code
            </label>
            <input
              type="text"
              value={inviteCode}
              onChange={(e) => {
                setInviteCode(e.target.value.toUpperCase());
                setError('');
              }}
              onKeyPress={handleKeyPress}
              placeholder="e.g., ABC123"
              maxLength={6}
              disabled={loading}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-600 focus:border-transparent outline-none text-center text-lg font-semibold tracking-wider disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <p className="text-gray-500 text-sm mt-2">6-character code (letters and numbers)</p>
          </div>

          <button
            onClick={handleJoin}
            disabled={!inviteCode.trim() || loading}
            className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Joining...
              </>
            ) : (
              'Join Company'
            )}
          </button>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Don&apos;t have an invite code?</span> Ask your company admin to send you one.
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          You can find the invite code in the email sent by your company admin
        </p>
      </div>
    </div>
  );
}
